<template>
  <div
    v-if="!count"
    class="choose-experience-banner choose-experience-banner--empty"
    :class="{ 'choose-experience-banner--full': fullWidth, 'choose-experience-banner--pinned': pinned }"
  >
    <div class="choose-experience-banner__text">
      <p class="choose-experience-banner__title">{{ t('banner.emptyTitle') }}</p>
      <p class="choose-experience-banner__subtitle">
        {{ city ? t('banner.emptySubtitleInCity', { city }) : t('banner.emptySubtitle') }}
      </p>
    </div>
    <UiButton
      v-view-all-experiences-click
      :text="t('banner.button')"
      :color-mode="UiButtonColorMode.NEUTRAL"
      :variant="UiButtonVariant.PRIMARY"
      :size="UiButtonSize.M"
      :is-full-width="!fullWidth || isMobile"
      :href="emptyHref || href"
    />
  </div>
  <div
    v-else
    class="choose-experience-banner"
    :class="{ 'choose-experience-banner--full': fullWidth, 'choose-experience-banner--pinned': pinned }"
  >
    <p class="choose-experience-banner__caption">{{ t('banner.caption') }}</p>
    <div class="choose-experience-banner__card">
      <div class="choose-experience-banner__stats">
        <p class="choose-experience-banner__count">{{ count }}</p>
        <p class="choose-experience-banner__noun">{{ t('banner.countNoun', count) }}</p>
      </div>
      <UiButton
        v-view-all-experiences-click
        :title="t('banner.button')"
        :color-mode="UiButtonColorMode.NEUTRAL"
        :variant="UiButtonVariant.SECONDARY"
        :size="UiButtonSize.M"
        :href="href"
        is-icon-only
      >
        <template #icon>
          <IconBase name="right-arrow" :size="20" />
        </template>
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UiButton, UiButtonColorMode, UiButtonSize, UiButtonVariant } from '@experience/core-button-ui';
import { useI18n } from '@experience/i18n';

import { useAnalytics } from '@/shared/lib/analytics';
import { IconBase } from '@/shared/ui/iconBase';
import { useScreenSize } from '@/shared/utils/breakpoints';

import ru from '../i18n/ru.json';

withDefaults(
  defineProps<{
    count: number;
    href: string;
    /** Город в предложном падеже («в Санкт-Петербурге») для пустого состояния. */
    city?: string;
    /** Куда ведёт кнопка, если экскурсий нет (список других мест). */
    emptyHref?: string;
    fullWidth?: boolean;
    /** Прилипает к низу экрана и тянется на всю ширину обёртки (планшет и мобильный). */
    pinned?: boolean;
  }>(),
  {
    city: '',
    emptyHref: '',
    fullWidth: false,
    pinned: false,
  },
);

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { isMobile } = useScreenSize();
const { useImmediateClickTracker } = useAnalytics();
// ссылка уводит со страницы, батч не успевает уйти
const vViewAllExperiencesClick = useImmediateClickTracker('sight:view-all-experiences.click');
</script>

<style scoped lang="scss">
.choose-experience-banner {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 248px;

  // на планшете и мобильном баннер в потоке (full) и такой же, прилипающий к низу экрана (pinned).
  // Планшет: в строку, отступы 20; мобильный: в колонку, отступы 16 и зазор 12 (Figma)
  &--full {
    flex-direction: row;
    gap: var(--alias-space-16, 16px);
    align-items: center;
    justify-content: space-between;
    width: auto;
    max-width: none;
    padding: var(--alias-space-20, 20px);
    background-color: var(--alias-color-background-subtle);
    border-radius: var(--alias-radius-8, 8px);

    // ширина текста ограничена, чтобы он держался в две строки (в макете 271px)
    .choose-experience-banner__caption {
      flex: 0 1 280px;
      min-width: 0;
      max-width: 280px;
    }

    .choose-experience-banner__card {
      flex-shrink: 0;
      align-self: auto;
      padding: var(--alias-space-12, 12px);
      background-color: var(--alias-color-background-subtler);
    }

    @include mobile {
      flex-direction: column;
      gap: var(--alias-space-12, 12px);
      align-items: stretch;
      padding: var(--alias-space-16, 16px);

      .choose-experience-banner__caption {
        flex: none;
        width: 100%;
      }

      .choose-experience-banner__card {
        flex-shrink: 1;
        align-self: stretch;
      }
    }
  }

  // липнет к низу экрана на всю ширину обёртки и заканчивается вместе с контентом
  &--pinned {
    position: sticky;
    bottom: 0;
    z-index: 10;
    padding-bottom: calc(var(--alias-space-20, 20px) + env(safe-area-inset-bottom, 0px));
    margin-inline: calc(-1 * var(--wrap-offset, 20px));
    border-top: 1px solid var(--alias-color-border-default, rgb(0 0 0 / 10%));
    border-radius: 0;

    @include mobile {
      padding-bottom: calc(var(--alias-space-16, 16px) + env(safe-area-inset-bottom, 0px));
    }
  }
}

// в конце контента закреплённый баннер снова обычный блок: не шире колонки контента
.choose-experience-banner--full:not(.choose-experience-banner--pinned) {
  max-width: 805px;
}

// экскурсий нет: вместо счётчика приглашаем посмотреть другие места
// на десктопе блок в колонке (кнопка на всю ширину), на планшете и мобильном он такой же
// полоса, как баннер с экскурсиями: в строку на планшете и в колонку на мобильном
.choose-experience-banner--empty:not(.choose-experience-banner--full) {
  align-items: stretch;
}

.choose-experience-banner__text {
  min-width: 0;
}

.choose-experience-banner__title {
  margin: 0;
  color: var(--alias-color-text-default);

  @include typography.base-400-300-medium;
}

.choose-experience-banner__subtitle {
  align-self: stretch;
  margin: 8px 0 0;
  color: var(--alias-color-text-default);

  @include typography.base-300-300-regular;
}

.choose-experience-banner__caption {
  margin: 0;
  color: var(--alias-color-text-default);

  @include typography.base-400-300-regular;
}

.choose-experience-banner__card {
  display: flex;
  gap: var(--alias-space-12, 12px);
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
  padding: var(--alias-space-20, 20px);
  background-color: var(--alias-color-background-subtle);
  border-radius: var(--alias-radius-8, 8px);
}

.choose-experience-banner__stats {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  min-width: 0;
}

.choose-experience-banner__count {
  margin: 0;
  color: var(--alias-color-text-default);

  @include typography.base-800-800-semi;
}

.choose-experience-banner__noun {
  margin: 0;
  color: var(--alias-color-text-default);

  @include typography.base-400-300-regular;
}
</style>
